import { Grid, Paper } from '@mui/material'
import { Typography } from '@vapor/material'
import { Box } from '@mui/system'
import { LocalizedString } from 'hic-components'
import { t } from 'i18next'
import { useState, useCallback, useEffect } from 'react'
import { Currency } from 'src/api/currency'
import { ReservationResourceAvailability, ReservationRate, ReservationCartRow } from '../../form/reservation-new.type'
import ReservationAccordionDetails from './reservation-accordion-details'
import ReservationAccordionHeader from './reservation-accordion-header'
import { Trans } from 'react-i18next'
import ReservationAccordionHeaderMobile from './reservation-accordion-header-mobile'
import useBreakpoints from 'src/app/hooks/breakpoints/use-breakpoints'

type ReservationAccordionProps = {
  isPage: boolean
  resources: ReservationResourceAvailability[]
  rates: ReservationRate[]
  nights: number
  adults: number
  children: { childId: string; quantity: number }[]
  checkIn: Date
  checkOut: Date
  currency: Currency
  cartRows: ReservationCartRow[]
  addCartRow: (row: ReservationCartRow, showSuccessMessage: boolean) => void
  navigateToAdditionalServices: () => void
  planningResourceTypeId?: string
}

export const rtUnavailableMagicNumber = 9999

export default function ReservationAccordion(props: ReservationAccordionProps) {
  const { isPage } = props
  const [accordionStates, setAccordionStates] = useState<{ id: string; open: boolean }[]>([])
  const { sm } = useBreakpoints()

  const resourceAvailability = (resourceTypeId: string) => {
    if (props.resources) {
      const res = props.resources.find((r) => r.resourceType.id === resourceTypeId)
      if (!res) return 0
      const cartEntries = props.cartRows.filter((row) => row.resourceType.id === res.resourceType.id)
      return res.availability - cartEntries.length
    }
    return 0
  }

  const disabledForBooking = (resourceTypeId: string) => {
    if (props.resources) {
      const res = props.resources.find((r) => r.resourceType.id === resourceTypeId)
      if (!res) return true
      return res.availability === rtUnavailableMagicNumber && (res.dailyAvailability ?? []).length === 0 && res.inventory === 0
    }
    return true
  }

  const resourceTypeAvailabilityString = (resourceTypeId: string, resourceInventory: number) => {
    if (!disabledForBooking(resourceTypeId)) {
      return <Trans i18nKey="pms.booking.reservation_new.availability" values={{ current: resourceAvailability(resourceTypeId), max: resourceInventory }} />
    } else {
      return t('pms.booking.reservation_new.resource_type_unavailable')
    }
  }

  const resourceUnavailable = (resourceTypeId: string) => {
    return resourceAvailability(resourceTypeId) <= 0 || disabledForBooking(resourceTypeId)
  }

  const accordionDetails = useCallback(
    (resourceTypeId: string, resourceTypeName: LocalizedString, resourceIndex: number, resourceTypeCode?: string) => {
      const firstInsertedRow = props.cartRows
        .map((r) => {
          return {
            isFirstInsertion: r.isFirstInsertion,
            rateId: r.rate.id,
            chargeCategoryId: r.chargeCategory.id,
          }
        })
        .find((r) => r.isFirstInsertion)

      return (
        <ReservationAccordionDetails
          resourceTypeId={resourceTypeId}
          resourceTypeName={resourceTypeName}
          resourceTypeCode={resourceTypeCode}
          rates={props.rates}
          nights={props.nights}
          adults={props.adults}
          children={props.children}
          checkIn={props.checkIn}
          checkOut={props.checkOut}
          currency={props.currency}
          firstInsertedCartRow={firstInsertedRow}
          addCartRow={(row: ReservationCartRow, showSuccessMessage: boolean) => props.addCartRow({ ...row, unavailable: row.unavailable || disabledForBooking(resourceTypeId) }, showSuccessMessage)}
          unavailable={resourceUnavailable(resourceTypeId)}
          navigateToAdditionalServices={props.navigateToAdditionalServices}
        />
      )
    },
    [props.nights, props.adults, props.children, props.checkIn, props.checkOut, props.rates, props.currency, props.cartRows, props.addCartRow, props.navigateToAdditionalServices],
  )

  const onToggleAccordion = (resourceTypeId: string) => {
    const stateIndex = accordionStates.findIndex((state) => state.id === resourceTypeId)
    if (stateIndex >= 0) {
      accordionStates[stateIndex].open = !accordionStates[stateIndex].open
    } else {
      accordionStates.push({
        id: resourceTypeId,
        open: true,
      })
    }
    setAccordionStates([...accordionStates])
  }

  useEffect(() => {
    const accordionStates =
      props.resources.map<{ id: string; open: boolean }>((r, i) => {
        const isAvailable = !resourceUnavailable(r.resourceType.id)
        const comingFromPlanning = props.planningResourceTypeId !== undefined
        const isTheOneFromPlanning = r.resourceType.id === props.planningResourceTypeId
        const open = isTheOneFromPlanning || (!comingFromPlanning && isAvailable)
        return { id: r.resourceType.id, open: open }
      }) ?? []
    setAccordionStates([...accordionStates])
  }, [props.resources])

  const calculateStartingFromAvgAndTotal = useCallback(
    (resourceTypeId: string): { avg: number; total: number } | undefined => {
      let startingFromAvg: number | undefined = undefined
      let startingFromTotal: number | undefined = undefined

      if (props.rates && props.rates.length > 0) {
        const filteredRates = props.rates?.filter((rate) => {
          const index = rate.resourceRates?.findIndex((resRate) => resRate.resourceTypeId === resourceTypeId)
          if (index !== undefined && index >= 0) {
            return Boolean(rate.resourceRates?.[index].dailyPrice?.length) && !rate.resourceRates?.[index]?.restrictionsNotMet?.includes('STOPSELL_CHARGE')
          }
          return false
        })
        for (const rate of filteredRates) {
          if (rate && rate.resourceRates && rate.resourceRates.length > 0) {
            const filteredResourceRates = rate.resourceRates.filter((resRate) => {
              return resRate.resourceTypeId === resourceTypeId && resRate.bookable && Boolean(resRate.dailyPrice?.length) && !resRate?.restrictionsNotMet?.includes('STOPSELL_CHARGE')
            })
            for (const resRate of filteredResourceRates) {
              const avg = resRate.dailyPrice.map((dp) => dp.price).reduce((acc, price) => acc + price, 0) / resRate.dailyPrice.length

              if (startingFromAvg === undefined || startingFromAvg > avg) {
                startingFromAvg = avg
                startingFromTotal = avg * props.nights
              }
            }
          }
        }
      }
      if (startingFromAvg && startingFromTotal) return { avg: startingFromAvg, total: startingFromTotal }
      return undefined
    },
    [props.rates, props.nights],
  )

  return (
    <Grid container>
      {props.resources.length === 0 && (
        <Grid item xs={12}>
          <Paper style={{ height: 87, display: 'flex', alignItems: 'center' }}>
            <Typography variant="titleSmall" style={{ paddingLeft: 20 }}>
              {t('pms.booking.reservation_new.no_resources_placeholder')}
            </Typography>
          </Paper>
        </Grid>
      )}

      {props.resources
        .slice()
        .sort((first, second) => {
          const isTheFirst = first.resourceType.id === props.planningResourceTypeId
          const isTheSecond = second.resourceType.id === props.planningResourceTypeId

          const firstStartingFrom = calculateStartingFromAvgAndTotal(first.resourceType.id)?.total ?? 999999
          const secondStartingFrom = calculateStartingFromAvgAndTotal(second.resourceType.id)?.total ?? 999999

          const firstUnavailable = resourceUnavailable(first.resourceType.id) ? 1 : 0
          const secondUnavailable = resourceUnavailable(second.resourceType.id) ? 1 : 0

          return Number(isTheSecond) - Number(isTheFirst) || firstUnavailable - secondUnavailable || firstStartingFrom - secondStartingFrom
        })
        ?.map((resource, resourceIndex) => {
          const startingFrom = calculateStartingFromAvgAndTotal(resource.resourceType.id)

          return (
            <Grid item xs={12} key={'resource' + resourceIndex}>
              <Paper
                variant="outlined"
                sx={{
                  p: { xs: 0.5, sm: 1 },
                  backgroundColor: 'var(--AzureishWhite) !important',
                  mb: 2,
                  pointerEvents: 'none',
                  border: resourceUnavailable(resource.resourceType.id) ? `1px solid red` : '0px',
                }}
              >
                {sm && (
                  <ReservationAccordionHeader
                    currency={props.currency}
                    onToggleAccordion={() => onToggleAccordion(resource.resourceType.id)}
                    isOpen={(accordionStates.find((state) => state.id === resource.resourceType.id)?.open ?? false) === true}
                    startingFromAvg={startingFrom?.avg}
                    startingFromTotal={startingFrom?.total}
                    isPage={isPage}
                    resourceIndex={resourceIndex}
                    resource={resource}
                    resourceUnavailable={resourceUnavailable}
                    resourceTypeAvailabilityString={resourceTypeAvailabilityString}
                    isDisabled={false}
                  />
                )}
                {!sm && (
                  <ReservationAccordionHeaderMobile
                    currency={props.currency}
                    onToggleAccordion={() => onToggleAccordion(resource.resourceType.id)}
                    isOpen={(accordionStates.find((state) => state.id === resource.resourceType.id)?.open ?? false) === true}
                    startingFromAvg={startingFrom?.avg}
                    startingFromTotal={startingFrom?.total}
                    isPage={isPage}
                    resourceIndex={resourceIndex}
                    resource={resource}
                    resourceUnavailable={resourceUnavailable}
                    resourceTypeAvailabilityString={resourceTypeAvailabilityString}
                    isDisabled={false}
                  />
                )}
                {accordionStates.find((state) => state.id === resource.resourceType.id)?.open && (
                  <Box>
                    {accordionDetails(resource.resourceType.id, resource.resourceType.customName ?? resource.resourceType.name.description, resourceIndex, resource.resourceType.code ?? undefined)}
                  </Box>
                )}
              </Paper>
            </Grid>
          )
        })}
    </Grid>
  )
}
